// frontend/src/components/production-sequence/RecalcDialog.tsx
// Modal centrado que aparece al seleccionar una OT: ofrece ver la producción en el
// Gantt o recalcular las fechas de la línea desde esa OT hacia abajo.
// Sustituye a la antigua barra flotante inferior.
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { calcularCapacidadDiaria } from '@/utils/sequenceCalculations';
import type { ProductionOrder, LineConfig } from '@/types/productionSequence';

interface Props {
  selectedOrder: ProductionOrder;
  lineConfig: LineConfig;
  canRecalc: boolean;
  onRecalc: (orderId: string) => void;
  onViewComponents: (order: ProductionOrder) => void;
  onDismiss: () => void;
  dark: boolean;
}

export function RecalcDialog({ selectedOrder, lineConfig, canRecalc, onRecalc, onViewComponents, onDismiss, dark }: Props) {
  const { t } = useTranslation();
  const confirmRef = useRef<HTMLButtonElement>(null);

  const cap = calcularCapacidadDiaria(lineConfig);
  const capInvalid = cap <= 0;
  const disabled = !canRecalc || capInvalid;

  // Foco inicial en el botón principal (solo al montar, no en cada render).
  useEffect(() => { confirmRef.current?.focus(); }, []);

  // Cerrar con Esc.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onDismiss(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onDismiss]);

  const panel = dark ? 'bg-slate-900 border-white/10 text-slate-200' : 'bg-white border-slate-200 text-slate-700';
  const muted = dark ? 'text-slate-400' : 'text-slate-500';
  const summaryBox = dark ? 'bg-white/5' : 'bg-slate-50';
  const btnPrimary = 'text-[13px] font-semibold px-4 py-2 rounded-lg cursor-pointer bg-sky-500 text-white hover:bg-sky-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors';
  const btnGhost = `text-[13px] font-semibold px-4 py-2 rounded-lg cursor-pointer transition-colors ${dark ? 'bg-white/8 text-slate-300 hover:bg-white/12' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm plan-overlay-enter"
      onClick={onDismiss}
      role="dialog"
      aria-modal="true"
    >
      <div
        className={`plan-panel-enter w-full max-w-md rounded-2xl border shadow-2xl p-5 ${panel}`}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-start gap-3">
          <span className="text-2xl leading-none mt-0.5">🎯</span>
          <div className="min-w-0 flex-1">
            <h2 className="text-[15px] font-semibold">
              {t('sequence.actions.title', { ot: selectedOrder.id })}
            </h2>
            <p className={`mt-1 text-[12px] leading-relaxed ${muted}`}>
              {t('sequence.actions.hint')}
            </p>
          </div>
        </div>

        {canRecalc && (
          <div className={`mt-4 rounded-xl px-3 py-2.5 text-[12px] ${summaryBox} ${muted}`}>
            {t('sequence.recalc.configSummary', {
              line: lineConfig.lineId,
              turnos: lineConfig.turnos,
              ud: lineConfig.udTurnoPromedio.toLocaleString('es-ES'),
              cap: cap.toLocaleString('es-ES'),
            })}
          </div>
        )}

        {canRecalc && capInvalid && (
          <p className="mt-3 text-[12px] font-semibold text-red-500">{t('sequence.recalc.capacityError')}</p>
        )}

        <div className="mt-5 flex items-center justify-end gap-2">
          <button onClick={onDismiss} className={btnGhost}>{t('sequence.recalc.cancel')}</button>
          <button
            ref={confirmRef}
            onClick={() => onViewComponents(selectedOrder)}
            className={btnPrimary}
          >
            🧩 {t('sequence.actions.viewComponents')}
          </button>
          <button
            onClick={() => onRecalc(selectedOrder.id)}
            disabled={disabled}
            className={btnPrimary}
            title={!canRecalc ? t('sequence.recalc.allLinesTooltip') : undefined}
          >
            📅 {t('sequence.recalc.do')}
          </button>
        </div>
      </div>
    </div>
  );
}
